import {Box, IconButton, InputAdornment, TextField} from '@mui/material';
import {
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {ReactComponent as ClearIcon} from '../../assests/svg/clear-Icon-filled.svg';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AurTooltip from '../common/aur-tooltip/AurTooltip';
import AurButton from '../common/aur-button/AurButton';
import AurDatePicker, {
  SelectedDateType,
} from '../common/aur-date-picker/aur-date-picker';
import {
  AUR_DATE_PICKER_OPTIONS,
  COL_TYPE,
  FILTER_OPERATORS,
  FILTER_TYPE,
} from '../../constants/constants';
import {t} from 'i18next';
import React from 'react';

type FilterFieldProps = {
  gridRenderHeaderFilterProps: GridRenderHeaderFilterProps;
  dataType: string;
  UIDataType: string;
};

const CustomDocListFilters = ({
  gridRenderHeaderFilterProps,
  dataType,
  UIDataType,
}: FilterFieldProps) => {
  const {colDef, inputRef} = gridRenderHeaderFilterProps;
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const [showFilterIcon, setShowFilterIcon] = useState(true);
  const [showClearIcon, setClearIcon] = useState(false);
  const [value, setValue] = useState('');
  const [dateOperator, setDateOperator] = useState('');
  const [filterMode, setFilterMode] = useState(FILTER_TYPE.normal);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dateAdvanceBtn, setDateAdvanceBtn] = useState(false);
  const [selectedDateArray, setSelectedDateArray] =
    useState<SelectedDateType[]>();

  // Find the current filters applied to this column
  const currentFieldFilters = useMemo(
    () =>
      filterModel.items?.filter(
        (item) => item.field === colDef.field && item.value
      ),
    [colDef.field, filterModel.items]
  );

  useEffect(() => {
    if (currentFieldFilters.length > 0) {
      setShowFilterIcon(false);
      setClearIcon(true);
      if (
        currentFieldFilters.length == 1 &&
        (currentFieldFilters[0].operator == FILTER_OPERATORS.contains ||
          currentFieldFilters[0].operator == FILTER_OPERATORS.signEqual ||
          currentFieldFilters[0].operator == FILTER_OPERATORS.is)
      ) {
        setFilterMode(FILTER_TYPE.normal);
        setValue(currentFieldFilters[0].value);
        setDateAdvanceBtn(false);
        if (colDef.type == COL_TYPE.date) {
          setDateOperator(currentFieldFilters[0].operator);
        }
      } else {
        if (colDef.type == COL_TYPE.date) {
          setFilterMode(FILTER_TYPE.normal);
          setDateAdvanceBtn(true);
          setValue(currentFieldFilters[0].value);
          setDateOperator(currentFieldFilters[0].operator);
        } else {
          setFilterMode(FILTER_TYPE.advanced);
        }
      }
    } else {
      setShowFilterIcon(true);
      setClearIcon(false);
      setFilterMode(FILTER_TYPE.normal);
      setValue('');
      setDateAdvanceBtn(false);
    }
  }, [filterModel, currentFieldFilters]);

  const handleClear = useCallback(() => {
    setValue('');
    apiRef.current.deleteFilterItem(currentFieldFilters[0]);
  }, [apiRef, colDef.field, currentFieldFilters]);

  const handleReturnPressed = (value: string) => {
    setFilterMode(FILTER_TYPE.normal);
    if (!value) {
      if (currentFieldFilters[0]) {
        apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }
      return;
    }

    const updatedFilterModel = {
      ...filterModel,
      items: [
        // Preserve all existing filters, except the one for this column
        ...filterModel.items.filter((item) => item.field !== colDef.field),
        {
          field: colDef.field,
          operator:
            dataType == COL_TYPE.keyword
              ? FILTER_OPERATORS.contains
              : colDef.type == COL_TYPE.string
                ? FILTER_OPERATORS.contains
                : colDef.type == COL_TYPE.number
                  ? FILTER_OPERATORS.signEqual
                  : FILTER_OPERATORS.is,
          value: value,
        },
      ],
    };
    apiRef.current.setFilterModel(updatedFilterModel);
    apiRef.current.setColumnHeaderFilterFocus('');
    apiRef.current.setColumnHeaderFocus('');
  };

  return (
    <>
      <Box className="custom-header-filter-container">
        <Box className="input-container">
          {filterMode == FILTER_TYPE.normal ? (
            colDef.type != COL_TYPE.date ? (
              <label>
              <TextField
                inputRef={inputRef}
                key={colDef.field}
                value={value}
                name={colDef.field}
                type={
                  colDef.type == COL_TYPE.number
                    ? COL_TYPE.number
                    : COL_TYPE.text
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setValue(event.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleReturnPressed(value);
                  }
                }}
                variant="outlined"
                placeholder={t('ecaDocList.filters.filter')}
                size="small"
                fullWidth
                inputProps={{
                  'data-testid': 'eca-header-filter-text',
                }}
                InputProps={{
                  endAdornment: showClearIcon && (
                    <InputAdornment position="end">
                        <ClearIcon onClick={handleClear} aria-label={t('common.clear')} />
                    </InputAdornment>
                  ),
                }}
              />
              </label>
            ) : colDef.type == COL_TYPE.date &&
              UIDataType == COL_TYPE.dateTime ? (
              <>
                <div 
                  onClick={(event) =>
                    setAnchorEl(anchorEl ? null : event.currentTarget)
                  }
                >
                  <AurDatePicker
                    type="query-date-picker"
                    operatorMenuClassName={'eca-date-filter-menu'}
                    placeholder={t('ecaDocList.filters.filter')}
                    headerFilter={true}
                    advanceLink={false}
                    showAdvance={dateAdvanceBtn}
                    selectedValue={value}
                    selectedDate={selectedDateArray}
                    selectedOperator={dateOperator}
                    datePickerOption={AUR_DATE_PICKER_OPTIONS.DATE_TIME}
                    dateFormat="YYYY-MM-DD HH:mm:ss"
                    delimiterBetweenOperator="|"
                    onApplyClick={(dates, operator) => {
                      setSelectedDateArray(dates);
                      const dateValue =
                        operator == FILTER_OPERATORS.between
                          ? `${dates[0]?.format('YYYY-MM-DD HH:mm:ss')}${dates[1] ? `|${dates[1]?.format('YYYY-MM-DD HH:mm:ss')}` : ''}`
                          : `${dates[0]?.format('YYYY-MM-DD HH:mm:ss')}`;
                      const currentDateFilters = filterModel.items?.filter(
                        (item) => item.field === colDef.field
                      );
                      if (dateValue == 'undefined') {
                        if (currentDateFilters[0]) {
                          apiRef.current.deleteFilterItem(
                            currentDateFilters[0]
                          );
                        }
                      } else {
                        const existingItems = [...filterModel.items];
                        const itemIndex = existingItems.findIndex(
                          (filterItem) => filterItem.field === colDef.field
                        );
                        const item = {
                          field: colDef.field,
                          operator: operator ? operator : '',
                          value: dateValue,
                        };
                        if (itemIndex === -1) {
                          existingItems.push(item);
                        } else {
                          existingItems[itemIndex] = item;
                        }
                        apiRef.current.setFilterModel(
                          {...filterModel, items: existingItems},
                          'upsertFilterItems'
                        );
                      }
                    }}
                    onCancelClick={() => {
                      const currentDateFilters = filterModel.items?.filter(
                        (item) => item.field === colDef.field
                      );
                      if (currentDateFilters[0]) {
                        apiRef.current.deleteFilterItem(currentDateFilters[0]);
                      }
                    }}
                  />
                </div>
              </>
            ) : colDef.type == COL_TYPE.date && UIDataType == COL_TYPE.time ? (
              <>
                <div
                  onClick={(event) =>
                    setAnchorEl(anchorEl ? null : event.currentTarget)
                  }
                >
                  <AurDatePicker
                    type="query-date-picker"
                    operatorMenuClassName={'eca-date-filter-menu'}
                    placeholder={t('ecaDocList.filters.filter')}
                    headerFilter={true}
                    advanceLink={false}
                    showAdvance={dateAdvanceBtn}
                    selectedValue={value}
                    selectedDate={selectedDateArray}
                    selectedOperator={dateOperator}
                    datePickerOption={AUR_DATE_PICKER_OPTIONS.STRICK_TIME}
                    dateFormat="HH:mm:ss"
                    delimiterBetweenOperator="|"
                    onApplyClick={(dates, operator) => {
                      setSelectedDateArray(dates);
                      const dateValue =
                        operator == FILTER_OPERATORS.between
                          ? `${dates[0]?.format('HH:mm:ss')}${dates[1] ? `|${dates[1]?.format('HH:mm:ss')}` : ''}`
                          : `${dates[0]?.format('HH:mm:ss')}`;
                      const currentDateFilters = filterModel.items?.filter(
                        (item) => item.field === colDef.field
                      );
                      if (dateValue == 'undefined') {
                        if (currentDateFilters[0]) {
                          apiRef.current.deleteFilterItem(
                            currentDateFilters[0]
                          );
                        }
                      } else {
                        const existingItems = [...filterModel.items];
                        const itemIndex = existingItems.findIndex(
                          (filterItem) => filterItem.field === colDef.field
                        );
                        const item = {
                          field: colDef.field,
                          operator: operator ? operator : '',
                          value: dateValue,
                        };
                        if (itemIndex === -1) {
                          existingItems.push(item);
                        } else {
                          existingItems[itemIndex] = item;
                        }
                        apiRef.current.setFilterModel(
                          {...filterModel, items: existingItems},
                          'upsertFilterItems'
                        );
                      }
                    }}
                    onCancelClick={() => {
                      const currentDateFilters = filterModel.items?.filter(
                        (item) => item.field === colDef.field
                      );
                      if (currentDateFilters[0]) {
                        apiRef.current.deleteFilterItem(currentDateFilters[0]);
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              colDef.type == COL_TYPE.date &&
              UIDataType == COL_TYPE.date && (
                <>
                  <div
                    onClick={(event) =>
                      setAnchorEl(anchorEl ? null : event.currentTarget)
                    }
                  >
                    <AurDatePicker
                      type="query-date-picker"
                      operatorMenuClassName={'eca-date-filter-menu'}
                      placeholder={t('ecaDocList.filters.filter')}
                      headerFilter={true}
                      advanceLink={false}
                      showAdvance={dateAdvanceBtn}
                      selectedValue={value}
                      dateFormat={'YYYY/MM/DD'}
                      selectedOperator={dateOperator}
                      delimiterBetweenOperator="|"
                      shouldShowDateInputWithinPopover
                      onApplyClick={(dates, operator) => {
                        const dateValue =
                          operator == FILTER_OPERATORS.between
                            ? `${dates[0]?.format('YYYY/MM/DD')}${dates[1] ? `|${dates[1]?.format('YYYY/MM/DD')}` : ''}`
                            : `${dates[0]?.format('YYYY/MM/DD')}`;
                        const currentDateFilters = filterModel.items?.filter(
                          (item) => item.field === colDef.field
                        );
                        if (dateValue == 'undefined') {
                          if (currentDateFilters[0]) {
                            apiRef.current.deleteFilterItem(
                              currentDateFilters[0]
                            );
                          }
                        } else {
                          const existingItems = [...filterModel.items];
                          const itemIndex = existingItems.findIndex(
                            (filterItem) => filterItem.field === colDef.field
                          );
                          const item = {
                            field: colDef.field,
                            operator: operator ? operator : '',
                            value: dateValue,
                          };
                          if (itemIndex === -1) {
                            existingItems.push(item);
                          } else {
                            existingItems[itemIndex] = item;
                          }
                          apiRef.current.setFilterModel(
                            {...filterModel, items: existingItems},
                            'upsertFilterItems'
                          );
                        }
                      }}
                      onCancelClick={() => {
                        const currentDateFilters = filterModel.items?.filter(
                          (item) => item.field === colDef.field
                        );
                        if (currentDateFilters[0]) {
                          apiRef.current.deleteFilterItem(
                            currentDateFilters[0]
                          );
                        }
                      }}
                    />
                  </div>
                </>
              )
            )
          ) : (
            <AurButton
              size="Small"
              type="text"
              label={t('ecaDocList.filters.advanced')}
              classNames="advanced-filter-button"
              onClick={() => {
                apiRef.current.showFilterPanel();
              }}
            />
          )}
        </Box>
        {colDef.type != COL_TYPE.date && (
          <Box className="button-container">
            <AurTooltip
              title={t('ecaDocList.filters.advanced')}
              aria-label={t('ecaDocList.filters.advanced')}
              className="typo-text label2_size tooltip-content"
              disableInteractive
              disableFocusListener
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -7],
                      },
                    },
                  ],
                },
              }}
            >
              <IconButton
                onClick={() => {
                  apiRef.current.showFilterPanel();
                }}
              >
                {showFilterIcon ? (
                  <FilterListIcon fontSize="small" />
                ) : (
                  <FilterAltIcon fontSize="small" />
                )}
              </IconButton>
            </AurTooltip>
          </Box>
        )}
      </Box>
    </>
  );
};

export default CustomDocListFilters;
